import { useDraggable } from '@dnd-kit/core';
import { ArrowLeftRight, CalendarCheck, CalendarPlus, Flame, GripVertical, Pencil } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import RunningWorkoutViz from './RunningWorkoutViz';
import { loadLevelStyle } from './blockWeekSessionStyles';

export default function BlockWeekSessionCard({
  session,
  busy = false,
  onEdit,
  onToggleSync,
  onReplace,
}) {
  const { t } = useLanguage();
  const styles = loadLevelStyle(session.load_level);
  const splits = session.user_running_exercise?.preview_splits || session.preview_splits || [];
  const displayTitle = session.user_running_exercise?.title || session.title;
  const hasWorkout = splits.length > 0;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `week-session-${session.id}`,
    data: {
      type: 'block-week-session',
      sessionId: session.id,
      scheduledDate: session.scheduled_date,
      session,
    },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)` }
    : undefined;

  const toggleSync = (e) => {
    e.stopPropagation();
    if (busy) return;
    onToggleSync?.(session, !session.calendar_synced);
  };

  const openEdit = (e) => {
    e.stopPropagation();
    if (busy || !session.user_running_exercise_id) return;
    onEdit?.(session);
  };

  const openReplace = (e) => {
    e.stopPropagation();
    if (busy) return;
    onReplace?.(session);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group rounded-lg border p-2 shadow-sm transition-shadow min-w-[8.5rem]',
        styles.card,
        isDragging && 'opacity-60 ring-2 ring-primary/40 shadow-md',
        busy && 'pointer-events-none opacity-70'
      )}
    >
      <div className='flex items-start gap-1'>
        <button
          type='button'
          className='mt-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-muted-foreground hover:bg-background/60'
          aria-label={t('periodization.week.dragSession')}
          {...listeners}
          {...attributes}
        >
          <GripVertical className='h-3.5 w-3.5' />
        </button>

        <div className='min-w-0 flex-1'>
          <div className='flex items-start gap-1'>
            <span className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', styles.dot)} />
            <p className='min-w-0 flex-1 text-[11px] font-medium leading-snug line-clamp-2' title={displayTitle}>
              {displayTitle}
            </p>
            {session.is_quality_session && (
              <Flame
                className='mt-0.5 h-3 w-3 shrink-0 text-rose-500'
                aria-label={t('periodization.week.qualitySession')}
              />
            )}
          </div>

          <div className='mt-1 flex flex-wrap items-center gap-1'>
            {session.block_session_role && (
              <Badge variant='outline' className='h-4 px-1 text-[9px] font-normal'>
                {session.block_session_role}
              </Badge>
            )}
            {hasWorkout ? (
              <Badge variant='secondary' className='h-4 px-1 text-[9px] font-normal'>
                {t('periodization.week.sessionFilled')}
              </Badge>
            ) : (
              <Badge variant='outline' className='h-4 px-1 text-[9px] font-normal'>
                {t('periodization.week.sessionScaffold')}
              </Badge>
            )}
          </div>

          <div className='mt-1.5 flex items-center gap-0.5'>
            <Button
              type='button'
              variant={session.calendar_synced ? 'secondary' : 'outline'}
              size='icon'
              className={cn('h-6 w-6', session.calendar_synced && 'text-primary')}
              title={
                session.calendar_synced
                  ? t('periodization.week.unsyncFromCalendar')
                  : t('periodization.week.syncToCalendar')
              }
              onClick={toggleSync}
            >
              {session.calendar_synced ? (
                <CalendarCheck className='h-3.5 w-3.5' />
              ) : (
                <CalendarPlus className='h-3.5 w-3.5' />
              )}
            </Button>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='h-6 w-6'
              title={t('periodization.week.replaceSession')}
              onClick={openReplace}
            >
              <ArrowLeftRight className='h-3.5 w-3.5' />
            </Button>
            {session.user_running_exercise_id && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6'
                title={t('runningExercise.editPlan')}
                onClick={openEdit}
              >
                <Pencil className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>

          {splits.length > 0 && (
            <div className='mt-2 overflow-hidden rounded border border-border/50 bg-background/50 p-1'>
              <RunningWorkoutViz splits={splits} heightPx={36} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
